import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { EntityViewLayout } from '../../components';
import { useApproveUser, useRejectUser, useUser } from '../../hooks/useUsers';
import { useToast } from '../../components/Toast';
import { authService } from '../../services/auth.service';
import { formatRole } from '../../utils/formatters';
import { USER_ROLES } from '@ministry-of-wine/shared';

const getErrorMessage = (error: unknown, fallback: string) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
};

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string;
  supplierId?: number;
  supplierName?: string;
  status: string;
  dateCreated: string;
  lastLogin?: string;
  deletedAt?: string | null;
}

export const UserDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: loading, renderState } = useUser(id as string, Boolean(id));
  const isSuperAdmin = authService.getStoredUser()?.role === USER_ROLES.SUPERADMIN;
  const approveUserMutation = useApproveUser();
  const rejectUserMutation = useRejectUser();
  const toast = useToast();
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);

  const handleApprove = async () => {
    try {
      await approveUserMutation.mutateAsync(id as string);
      toast.success('User approved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve user. Please try again.'));
    }
  };

  const handleConfirmReject = async () => {
    try {
      await rejectUserMutation.mutateAsync(id as string);
      toast.success('User rejected');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject user. Please try again.'));
    } finally {
      setShowRejectConfirm(false);
    }
  };
  
  const user: User | null = data
    ? {
        id: (data as any).id,
        firstName: (data as any).firstName,
        lastName: (data as any).lastName,
        email: (data as any).email,
        phone: (data as any).phone,
        role: (data as any).role,
        supplierId: (data as any).supplierId,
        supplierName: (data as any).supplierName,
        status: (data as any).status,
        dateCreated: (data as any).createdAt,
        lastLogin: (data as any).lastLogin,
        deletedAt: (data as any).deletedAt,
      }
    : null;

  if (renderState.status === 'loading') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading user details...</div>
      </div>
    );
  }

  if (renderState.status === 'error' || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">User not found</div>
      </div>
    );
  }

  const isSelf = Number(authService.getStoredUser()?.id) === Number(user.id);
  // Approve reinstates a pending/rejected user; Reject revokes a pending/active one.
  const canApprove =
    isSuperAdmin &&
    !user.deletedAt &&
    (user.status === 'pending_approval' || user.status === 'rejected');
  const canReject =
    isSuperAdmin &&
    !user.deletedAt &&
    !isSelf &&
    (user.status === 'pending_approval' || user.status === 'active');

  return (
    <EntityViewLayout
      closeTo="/users"
      backTo="/users"
      backLabel="Back to users"
      title={`${user.firstName} ${user.lastName}`}
      headerAction={
        <button
          onClick={() => navigate(`/users/${id}/edit`)}
          className="flex items-center gap-2 hover:opacity-90 transition-opacity"
          style={{
            height: '38px',
            borderRadius: '6px',
            backgroundColor: '#404040',
            border: '1px solid #404040',
            padding: '0 16px',
            color: '#EAECF0',
            fontFamily: 'Oxygen',
            fontSize: '14px',
            fontWeight: 400,
          }}
        >
          <Pencil size={20} color="#EAECF0" strokeWidth={1.67} aria-hidden="true" />
          Edit
        </button>
      }
    >
      {/* User Details Card */}
      <Card>
        <div style={{
          fontFamily: 'Oxygen',
          fontSize: '12px',
          fontWeight: 700,
          color: '#404040',
          letterSpacing: '0.05em',
          marginBottom: '24px'
        }}>
          USER DETAILS
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '12px',
                fontWeight: 400,
                color: '#667085',
                marginBottom: '4px'
              }}>
                Name
              </div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#101828'
              }}>
                {user.firstName} {user.lastName}
              </div>
            </div>

            <div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '12px',
                fontWeight: 400,
                color: '#667085',
                marginBottom: '4px'
              }}>
                Email
              </div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#101828'
              }}>
                {user.email}
              </div>
            </div>

            {user.phone && (
              <div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#667085',
                  marginBottom: '4px'
                }}>
                  Phone
                </div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#101828'
                }}>
                  {user.phone}
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '12px',
                fontWeight: 400,
                color: '#667085',
                marginBottom: '4px'
              }}>
                Role
              </div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#101828'
              }}>
                {formatRole(user.role)}
              </div>
            </div>

            {user.supplierName && (
              <div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#667085',
                  marginBottom: '4px'
                }}>
                  Supplier
                </div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#101828'
                }}>
                  <button
                    onClick={() => navigate(`/suppliers/${user.supplierId}`)}
                    className="text-blue-600 hover:underline"
                  >
                    {user.supplierName}
                  </button>
                </div>
              </div>
            )}

            <div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '12px',
                fontWeight: 400,
                color: '#667085',
                marginBottom: '4px'
              }}>
                Status
              </div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#101828'
              }}>
                {user.deletedAt ? (
                  <span
                    className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                  >
                    Deleted
                  </span>
                ) : user.status === 'rejected' ? (
                  <span
                    className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: '#FEE2E2', color: '#B42318' }}
                  >
                    Rejected
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: user.status === 'active' ? '#D1FAE5' : '#FEF3C7',
                      color: user.status === 'active' ? '#065F46' : '#92400E'
                    }}
                  >
                    {user.status === 'active' ? 'Active' : 'Pending approval'}
                  </span>
                )}
                {(canApprove || canReject) && (
                  <div className="mt-3 flex gap-3">
                    {canApprove && (
                      <button
                        onClick={handleApprove}
                        disabled={approveUserMutation.isPending}
                        aria-label="Approve user"
                        style={{
                          fontFamily: 'Oxygen',
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#067647',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        Approve
                      </button>
                    )}
                    {canReject && (
                      <button
                        onClick={() => setShowRejectConfirm(true)}
                        disabled={rejectUserMutation.isPending}
                        aria-label="Reject user"
                        style={{
                          fontFamily: 'Oxygen',
                          fontSize: '12px',
                          fontWeight: 400,
                          color: '#B42318',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        Reject
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '12px',
                fontWeight: 400,
                color: '#667085',
                marginBottom: '4px'
              }}>
                Date Created
              </div>
              <div style={{
                fontFamily: 'Oxygen',
                fontSize: '14px',
                fontWeight: 400,
                color: '#101828'
              }}>
                {new Date(user.dateCreated).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </div>
            </div>

            {user.lastLogin && (
              <div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#667085',
                  marginBottom: '4px'
                }}>
                  Last Login
                </div>
                <div style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#101828'
                }}>
                  {new Date(user.lastLogin).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <ConfirmDialog
        isOpen={showRejectConfirm}
        title="Reject user"
        message={
          <>
            Reject <strong>{user.firstName} {user.lastName}</strong>? They will be marked as
            rejected and will not be able to sign in.
          </>
        }
        confirmLabel="Reject"
        loadingLabel="Rejecting..."
        cancelLabel="Cancel"
        isLoading={rejectUserMutation.isPending}
        onConfirm={handleConfirmReject}
        onCancel={() => setShowRejectConfirm(false)}
      />
    </EntityViewLayout>
  );
};

export default UserDetailPage;
